import React from 'react'
import { Depths } from '@uifabric/fluent-theme/lib/fluent/FluentDepths'
import { SharedColors } from '@uifabric/fluent-theme/lib/fluent/FluentColors'
import { getTheme, ITheme } from '@uifabric/styling'
import {
  ActionButton,
  Stack,
  Spinner,
  SpinnerSize,
  Separator,
  INavLinkGroup,
  Nav,
  IRenderFunction,
  Text,
  INavLink,
} from 'office-ui-fabric-react'
import { ChildProps, graphql } from 'react-apollo'
import { CreateVideoCollectionDialog } from '../Screens/VideoCollectionScreen/CreateVideoCollectionDialog'
import { CreatePlacementCollectionDialog as CreatePlacementCollectionDialogv2 } from '../Components/CreatePlacementCollectionDialog_v2'
import { CreateCampaignCollectionDialog } from '../Screens/CampaignCollectionScreen/CreateCampaignCollectionDialog'
import {
  getAllCollectionSummariesQuery,
  AllCollectionSummariesResponse,
  AccountDisplayInfo,
  isAdmin,
} from '../Api/session'
import { SessionContext } from '../Contexts/SessionContext'
import { FilterableCollectionSidebar } from './FilterableCollectionSidebar'
import { isInIframe } from '../Utils/iframe'

type ModalType =
  | 'create-videos-collection'
  | 'create-outstream-placements-collection'
  | 'create-outstream-placements-collection_v2'
  | 'create-campaigns-collection'
  | 'create-instream-placements-collection'
  | 'create-instream-placements-collection_v2'

interface State {
  openModal?: ModalType
}

interface InputProps {
  minWidth: number
  mainPath: string[]
  selectedPath: string
  selectedState: SidebarState
  account?: AccountDisplayInfo
}

export type SidebarState = 'open' | 'closed'

class SidebarView extends React.Component<
  ChildProps<InputProps, AllCollectionSummariesResponse>,
  State
> {
  static contextType = SessionContext
  context!: React.ContextType<typeof SessionContext>
  theme: ITheme

  constructor(props: InputProps) {
    super(props)
    this.theme = getTheme()
    this.state = {
      openModal: undefined,
    }
  }
  shouldRenderVideoCollectionsSidebar() {
    return (
      this.props.selectedPath === 'video-assets' &&
      this.props.selectedState === 'open'
    )
  }
  shouldRenderOutstreamPlacementCollectionsSidebar() {
    return (
      this.props.selectedPath === 'outstream-placements' &&
      this.props.selectedState === 'open'
    )
  }
  shouldRenderOutstreamPlacementCollectionsSidebar_v2() {
    return (
      this.props.selectedPath === 'outstream-placements_v2' &&
      this.props.selectedState === 'open'
    )
  }
  shouldRenderInstreamPlacementCollectionsSidebar() {
    return (
      this.props.selectedPath === 'instream-placements' &&
      this.props.selectedState === 'open'
    )
  }
  shouldRenderInstreamPlacementCollectionsSidebar_v2() {
    return (
      this.props.selectedPath === 'instream-placements_v2' &&
      this.props.selectedState === 'open'
    )
  }
  shouldRenderCampaignsCollectionsSidebar() {
    return (
      this.props.selectedPath === 'campaigns' &&
      this.props.selectedState === 'open'
    )
  }
  shouldRenderSellersJsonSidebar() {
    return (
      this.props.selectedPath === 'sellers-json' &&
      this.props.selectedState === 'open'
    )
  }
  render(): JSX.Element {
    const { error, loading } = this.props.data!
    if (error) {
      window.console.error(error)
    }
    if (error || loading) {
      return this.renderLoading()
    }
    return (
      <Stack
        className="sidebar-container"
        horizontalAlign="start"
        verticalAlign="start"
        horizontal
        verticalFill
      >
        <Stack
          className="sidebar"
          horizontalAlign="start"
          verticalAlign="start"
          horizontal
          verticalFill
          style={{
            minWidth: this.props.minWidth,
            boxShadow: Depths.depth8,
            backgroundColor: this.theme.palette.white,
            paddingTop: 20,
            zIndex: 1,
            overflow: 'auto',
          }}
        >
          {this.renderMainNav()}
        </Stack>
        {this.shouldRenderVideoCollectionsSidebar() &&
          this.renderVideoCollectionsSidebar()}
        {this.shouldRenderOutstreamPlacementCollectionsSidebar_v2() &&
          this.renderOutstreamPlacementCollectionsSidebar_v2()}
        {this.shouldRenderInstreamPlacementCollectionsSidebar_v2() &&
          this.renderInstreamPlacementCollectionsSidebar_v2()}
        {this.shouldRenderCampaignsCollectionsSidebar() &&
          this.renderCampaignsCollectionsSidebar()}
        {this.shouldRenderSellersJsonSidebar() &&
          this.renderSellersJsonSidebar()}
        {this.state.openModal === 'create-videos-collection' &&
          this.renderCreateVideoCollectionDialog(this.props.data!.refetch)}
        {this.state.openModal === 'create-outstream-placements-collection_v2' &&
          this.renderCreateOutstreamPlacementCollectionDialog_v2(
            this.props.data!.refetch
          )}
        {this.state.openModal === 'create-instream-placements-collection_v2' &&
          this.renderCreateInstreamPlacementCollectionDialog_v2(
            this.props.data!.refetch
          )}
        {this.state.openModal === 'create-campaigns-collection' &&
          this.renderCreateCampaignsCollectionDialog(this.props.data!.refetch)}
      </Stack>
    )
  }
  renderMainNav(): JSX.Element {
    return (
      <Nav
        selectedKey={this.props.selectedPath}
        onRenderGroupHeader={
          this.renderHeader as IRenderFunction<INavLinkGroup>
        }
        styles={{
          root: {
            width: this.props.minWidth,
          },
        }}
        groups={
          [
            isInIframe() ? null : this.getHomeNavigationGroup(),
            this.getAdvertisingTargetsNavigationGroup(),
            this.getAdvertisingSourcesNavigationGroup(),
            this.getUsageReportNavigationGroup(),
            isAdmin(this.props.data!.session!)
              ? this.getAdminNavigationGroup()
              : undefined,
            this.getSupplyChainNavigationGroup(),
            this.getServiceReportNavigationGroup(),
          ].filter(Boolean) as INavLinkGroup[]
        }
      />
    )
  }
  renderVideoCollectionsSidebar(): JSX.Element {
    return this.renderCollectionSidebar(
      this.getVideoCollectionGroups(),
      'Create Video Assets Collection',
      () => this.setState({ openModal: 'create-videos-collection' })
    )
  }
  renderOutstreamPlacementCollectionsSidebar_v2(): JSX.Element {
    return this.renderCollectionSidebar(
      this.getOutstreamPlacementCollectionGroups_v2(),
      'Create Placements Collection',
      () =>
        this.setState({
          openModal: 'create-outstream-placements-collection_v2',
        })
    )
  }
  renderInstreamPlacementCollectionsSidebar_v2(): JSX.Element {
    return this.renderCollectionSidebar(
      this.getInstreamPlacementCollectionGroups_v2(),
      'Create Placements Collection',
      () =>
        this.setState({ openModal: 'create-instream-placements-collection_v2' })
    )
  }
  renderCampaignsCollectionsSidebar(): JSX.Element {
    return (
      <FilterableCollectionSidebar
        creationLabel="Create Campaigns Collection"
        onCreate={() =>
          this.setState({ openModal: 'create-campaigns-collection' })
        }
        selectedKey={this.props.mainPath[1]}
        groups={this.getCampaignsCollectionGroups()}
        minWidth={this.props.minWidth}
        renderHeader={this.renderHeader as IRenderFunction<INavLinkGroup>}
      />
    )
  }
  renderSellersJsonSidebar(): JSX.Element {
    return this.renderCollectionSidebar(
      this.getSellersJsonCollectionGroups(),
      ''
    )
  }
  renderCollectionSidebar(
    group: INavLinkGroup | INavLinkGroup[],
    creationLabel: string,
    onCreate?: () => void
  ): JSX.Element {
    return (
      <Stack
        className="sidebar-collection"
        horizontalAlign="start"
        verticalAlign="start"
        verticalFill
        style={{
          minWidth: this.props.minWidth,
          boxShadow: Depths.depth8,
          paddingTop: 20,
          zIndex: 0,
          overflow: 'auto',
        }}
      >
        <Nav
          selectedKey={this.props.mainPath[1]}
          onRenderGroupHeader={
            this.renderHeader as IRenderFunction<INavLinkGroup>
          }
          styles={{
            root: {
              width: this.props.minWidth,
              borderLeft: 1,
              borderLeftColor: this.theme.palette.blackTranslucent40,
            },
            groupContent: {
              marginBottom: 0,
            },
            linkText: {
              fontStyle: 'italic',
              paddingRight: 20,
            },
          }}
          groups={Array.isArray(group) ? group : [group]}
        />
        {!!onCreate && (
          <>
            <Separator styles={{ root: { width: '100%' } }}></Separator>
            <Stack
              horizontalAlign="center"
              verticalAlign="start"
              horizontal
              style={{ width: '100%' }}
            >
              <ActionButton
                iconProps={{ iconName: 'Add' }}
                onClick={onCreate}
                text={creationLabel}
              />
            </Stack>
          </>
        )}
      </Stack>
    )
  }
  renderCreateVideoCollectionDialog(refetch: () => void) {
    return (
      <CreateVideoCollectionDialog
        onUpdated={() => refetch()}
        onClose={() => this.setState({ openModal: undefined })}
      />
    )
  }
  renderCreateOutstreamPlacementCollectionDialog_v2(refetch: () => void) {
    // eslint-disable-next-line react/jsx-pascal-case
    return (
      <CreatePlacementCollectionDialogv2
        type="outstream"
        onUpdated={() => refetch()}
        onClose={() => this.setState({ openModal: undefined })}
      />
    )
  }
  renderCreateInstreamPlacementCollectionDialog_v2(refetch: () => void) {
    // eslint-disable-next-line react/jsx-pascal-case
    return (
      <CreatePlacementCollectionDialogv2
        type="instream"
        onUpdated={() => refetch()}
        onClose={() => this.setState({ openModal: undefined })}
      />
    )
  }
  renderCreateCampaignsCollectionDialog(refetch: () => void) {
    return (
      <CreateCampaignCollectionDialog
        onUpdated={() => refetch()}
        onClose={() => this.setState({ openModal: undefined })}
      />
    )
  }
  renderHeader(group: INavLinkGroup): JSX.Element {
    return (
      <Separator alignContent="start">
        <Text
          variant="small"
          style={{
            color: SharedColors.gray20,
          }}
        >
          {group.name}
        </Text>
      </Separator>
    )
  }
  getVideoCollectionGroups(): INavLinkGroup[] {
    const ownCollections = this.props.data!.videoCollections!.filter(
      ({ shared }) => !shared
    )
    const sharedCollections = this.props.data!.videoCollections!.filter(
      ({ shared }) => shared
    )
    const navigationGroups = [
      {
        name: 'Own Video Content Collections',
        links: [
          ...ownCollections.map((collection) => ({
            name: collection.title,
            url: `#/${this.context.ctok}/video-assets/${collection.id}`,
            key: collection.id,
          })),
        ],
      },
    ]
    if (sharedCollections.length > 0) {
      navigationGroups.push({
        name: 'Shared Video Content Collections',
        links: [
          ...sharedCollections.map((collection) => ({
            name: collection.title,
            url: `#/${this.context.ctok}/shared-video-assets/${collection.id}`,
            key: collection.id,
          })),
        ],
      })
    }
    return navigationGroups
  }
  getOutstreamPlacementCollectionGroups_v2(): INavLinkGroup {
    return {
      name: 'Outstream Placement Collections',
      links: [
        ...this.props.data!.outstreamPlacementCollections_v2!.map(
          (collection) => ({
            name: collection.title,
            url: `#/${this.context.ctok}/outstream-placements_v2/${collection.id}`,
            key: collection.id,
          })
        ),
      ],
    }
  }
  getInstreamPlacementCollectionGroups_v2(): INavLinkGroup {
    return {
      name: 'Instream Placement Collections',
      links: [
        ...this.props.data!.instreamPlacementCollections_v2!.map(
          (collection) => ({
            name: collection.title,
            url: `#/${this.context.ctok}/instream-placements_v2/${collection.id}`,
            key: collection.id,
          })
        ),
      ],
    }
  }
  getCampaignsCollectionGroups(): INavLinkGroup[] {
    const campaignCollections = this.props.data!.campaignCollections!
    const campaignCollectionGroups: Record<string, INavLinkGroup> = {}
    const campaignCollectionsLinks: INavLink[] = []

    const campaignCollectionToNavLink = ({
      title,
      id,
    }: {
      title: string
      id: string
    }) => {
      return {
        name: title,
        url: `#/${this.context.ctok}/campaigns/${id}`,
        key: id,
      }
    }

    if (
      !this.context.features.includes('allowCrossAccountCampaignsCollections')
    ) {
      for (const cc of campaignCollections) {
        campaignCollectionsLinks.push(campaignCollectionToNavLink(cc))
      }
    } else {
      for (const cc of campaignCollections) {
        const [account, ...rest] = cc.title.split('/')
        if (rest.length < 1) {
          campaignCollectionsLinks.push(campaignCollectionToNavLink(cc))
          continue
        }

        if (!campaignCollectionGroups[account]) {
          campaignCollectionGroups[account] = {
            name: account,
            links: [],
          }
        }

        campaignCollectionGroups[account].links!.push(
          campaignCollectionToNavLink({
            title: rest.join('/'),
            id: cc.id,
          })
        )
      }
    }

    return [
      {
        name: 'Campaign Collections',
        links: [
          {
            name: 'All Campaigns',
            url: `#/${this.context.ctok}/campaigns/all`,
            key: 'all',
          },
          ...campaignCollectionsLinks,
        ],
      },
      ...Object.values(campaignCollectionGroups),
    ]
  }
  getSellersJsonCollectionGroups(): INavLinkGroup {
    return {
      name: 'Sellers.json',
      links: [
        {
          name: 'Own Declaration',
          url: `#/${this.context.ctok}/sellers-json/own-declaration`,
          key: 'own-declaration',
        },
        {
          name: 'References from 3rd Party buyers',
          url: `#/${this.context.ctok}/sellers-json/references`,
          key: 'references',
        },
      ],
    }
  }
  getHomeNavigationGroup() {
    return {
      links: [
        {
          name: 'Home',
          url: `#/${this.context.ctok}`,
          key: 'home',
          icon: 'News',
        },
      ],
    }
  }
  getAdvertisingTargetsNavigationGroup() {
    const advertisingTargetsNavigationGroupLinks = []
    advertisingTargetsNavigationGroupLinks.push(
      {
        name: 'Integrations',
        url: `#/${this.context.ctok}/integrations`,
        key: 'integrations',
        icon: 'WebComponents',
      },
      {
        name: 'Outstream Placements',
        url: `#/${this.context.ctok}/${this.props.mainPath.join('/')}/${
          this.shouldRenderOutstreamPlacementCollectionsSidebar_v2()
            ? ''
            : '#/nav/outstream-placements_v2/open'
        }`,
        key: 'outstream-placements_v2',
        icon: 'tiles',
      },
      {
        name: 'Instream Placements',
        url: `#/${this.context.ctok}/${this.props.mainPath.join('/')}/${
          this.shouldRenderInstreamPlacementCollectionsSidebar_v2()
            ? ''
            : '#/nav/instream-placements_v2/open'
        }`,
        key: 'instream-placements_v2',
        icon: 'Slideshow',
      }
    )
    advertisingTargetsNavigationGroupLinks.push({
      name: 'Video Content',
      url: `#/${this.context.ctok}/${this.props.mainPath.join('/')}/${
        this.shouldRenderVideoCollectionsSidebar()
          ? ''
          : '#/nav/video-assets/open'
      }`,
      key: 'video-assets',
      icon: 'video',
    })
    advertisingTargetsNavigationGroupLinks.push({
      name: '3rd Party Inventory',
      url: `#/${this.context.ctok}/shared-inventory`,
      key: 'shared-inventory',
      icon: 'EngineeringGroup',
    })
    const advertisingTargetsNavigationGroup = {
      name: 'Advertising Targets',
      links: advertisingTargetsNavigationGroupLinks,
    }
    return advertisingTargetsNavigationGroup
  }
  getAdvertisingSourcesNavigationGroup() {
    if (!this.props.account) {
      return undefined
    }
    return {
      name: 'Advertising Sources',
      links: [
        {
          name: 'Campaigns',
          url: `#/${this.context.ctok}/${this.props.mainPath.join('/')}/${
            this.shouldRenderCampaignsCollectionsSidebar()
              ? ''
              : '#/nav/campaigns/open'
          }`,
          key: 'campaigns',
          icon: 'InternetSharing',
        },
      ],
    }
  }
  getUsageReportNavigationGroup() {
    const getDashboardLink = (name: string): string =>
      `https://platform.caroda.io/api/forwardAuth?redirect=${encodeURIComponent(
        `https://grafana.caroda.io/api/auth?token=%TOKEN%&dashboard=${name}`
      )}`

    const admin = isAdmin(this.props.data!.session!)
    let integratedWithInview = false
    let integratedWithR2B2 = false
    for (const integration of this.props.data!.integrations!) {
      if (integration.integratedWithInview) {
        integratedWithInview = true
      }
      if (integration.integratedWithR2B2) {
        integratedWithR2B2 = true
      }
      if (integratedWithInview && integratedWithR2B2) {
        break
      }
    }

    const hasInview = admin || integratedWithInview
    const hasR2B2 = admin || integratedWithR2B2

    const inviewDashboardUrl =
      'https://inviewapi.caroda.io/api/auth?token=%TOKEN%&dashboard=revenue'
    const redirectToInviewDashboardUrl = `https://platform.caroda.io/api/forwardAuth?redirect=${encodeURIComponent(
      inviewDashboardUrl
    )}`
    const redirectToR2B2DashboardUrl = `https://aym.r2b2.io/stats/overview${admin ? '/overview/all-publishers' : ''}`

    let revenueLinks: INavLink[] = []
    if (admin) {
      revenueLinks.push({
        name: 'Inview Revenue',
        url: redirectToInviewDashboardUrl,
        target: '_blank',
        icon: 'AreaChart',
      })
      revenueLinks.push({
        name: 'R2B2 monetization',
        url: redirectToR2B2DashboardUrl,
        target: '_blank',
        icon: 'AreaChart',
      })
    } else if (hasInview || hasR2B2) {
      const toR2B2 = hasR2B2 && !hasInview
      revenueLinks.push({
        name: toR2B2 ? 'R2B2 monetization' : 'Inview Revenue',
        url: toR2B2 ? redirectToR2B2DashboardUrl : redirectToInviewDashboardUrl,
        target: '_blank',
        icon: 'AreaChart',
      })
    }

    return {
      name: 'Usage Reports',
      links: [
        {
          name: 'Advertising',
          url: getDashboardLink('advertising'),
          target: '_blank',
          icon: 'AreaChart',
        },
        {
          name: 'Player',
          url: getDashboardLink('player'),
          target: '_blank',
          icon: 'AreaChart',
        },
        {
          name: 'Video Usage',
          url: getDashboardLink('video-usage'),
          target: '_blank',
          icon: 'AreaChart',
        },
        ...revenueLinks,
      ].filter(Boolean),
    }
  }
  getAdminNavigationGroup() {
    return {
      name: 'Admin',
      links: [
        {
          name: 'Experiments',
          url: `#/${this.context.ctok}/experiments`,
          key: 'experiments',
          icon: 'TestBeaker',
        },
        {
          name: 'Experimentation Groups',
          url: `#/${this.context.ctok}/experimentation-groups`,
          key: 'experimentation-groups',
          icon: 'AssessmentGroup',
        },
        {
          name: 'Experiment Overview',
          url: `#/${this.context.ctok}/experiment-overview`,
          key: 'experiment-overview',
          icon: 'TestSuite',
        },
      ],
    }
  }
  getSupplyChainNavigationGroup() {
    return {
      name: 'Supply Chain',
      links: [
        {
          name: 'Sellers.json',
          url: `#/${this.context.ctok}/${this.props.mainPath.join('/')}/${
            this.shouldRenderSellersJsonSidebar()
              ? ''
              : '#/nav/sellers-json/open'
          }`,
          key: 'sellers-json',
          icon: 'BranchMerge',
        },
      ],
    }
  }
  getServiceReportNavigationGroup() {
    return {
      name: 'Service Reports',
      links: [
        {
          name: 'Uptime',
          url: 'https://uptime.statuscake.com/?TestID=eUi8XugNTB',
          target: '_blank',
          icon: 'CloudUpload',
        },
      ],
    }
  }
  renderLoading() {
    return (
      <Stack
        className="sidebar"
        horizontalAlign="center"
        verticalAlign="center"
        verticalFill
        style={{
          minWidth: this.props.minWidth,
          boxShadow: Depths.depth8,
        }}
      >
        <Spinner size={SpinnerSize.large} />
      </Stack>
    )
  }
}

const withGql = graphql<InputProps, AllCollectionSummariesResponse>(
  getAllCollectionSummariesQuery,
  { options: () => ({}) }
)
const Sidebar = withGql(SidebarView)

export { Sidebar }
